import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from './../Glob';
import BaseStore from '../../../../_common/platform/ts/BaseStore';
import { Constants } from '../../../../_common/platform/ts/Constants';
import * as appTheme from '../../../../_common/platform/ts/appTheme';
import { apiCall } from '../../../../_common/platform/ts/AqlUtility';
import * as Utilities from '../../../../_common/platform/ts/Utilities';

import { AppDispatcher as Dispatcher } from '../dispatcher/AppDispatcher';
import { Action, AppActions, I, check as checkAction } from '../actions/AppActions';

export interface IThemesHashItem {
    id: number;
    name: string;
    type: string[];
}

export interface IThemeState {
    loadingThemes: boolean;
    creatingTheme: boolean;
    loadingTheme: any;
    updatingTheme: any;
    renamingTheme: any;
    deletingTheme: any;

    themesList: any[];
    themesHash: { [id: number]: IThemesHashItem };
    themesType: { [id: number]: string[] };
    themesData: { [id: number]: any };

    themesListDef: any;
}

export class ThemeStore extends BaseStore<IThemeState, AppActions, Dispatcher, IGlob> {
    public _state: IThemeState;
    public static _name = 'theme';

    public static getInitialState() {
        return {
            loadingThemes: false,
            creatingTheme: false,
            loadingTheme: {},
            updatingTheme: {},
            renamingTheme: {},
            deletingTheme: {},

            themesList: [],
            themesHash: {},
            themesData: {},
            themesType: {},

            themesListDef: null
        };
    }

    // It must be defined to make themes be loaded.
    public readCache() {}

    private themeGet(id: number, silent = false) {
        const def = $.Deferred<any>();

        if (this._state.loadingTheme[id] === true) {
            return;
        }

        this._state.loadingTheme[id] = true;

        apiCall(
            this.actions,
            'theme_Get',
            {
                id: id
            },
            null,
            def
        );

        def.done((resp) => {
            let vars;

            try {
                vars = JSON.parse(resp.result);
            } catch (e) {
                vars = {};
                this.actions.showNotification_Fail('Theme ' + id + ' is invalid');
            }

            this._state.themesData[id] = vars;
            this._state.themesType[id] = appTheme.getThemeType(vars);

            const obj = this._state.themesHash[id];

            if (obj != null) {
                obj.type = this._state.themesType[id];
            }

            if (silent !== true) {
                this.actions.themeGetFinish(true, id);
            }
        });

        def.fail((resp) => {
            delete this._state.themesData[id];
            delete this._state.themesType[id];

            if (silent !== true) {
                this.actions.themeGetFinish(false, id);
            }
        });

        def.always(() => {
            delete this._state.loadingTheme[id];
        });

        return def;
    }

    private themeGetFinish(success, id) {
        delete this._state.loadingTheme[id];
    }

    public getThemesHash() {
        return this._state.themesHash;
    }

    public getThemesData() {
        return this._state.themesData;
    }

    public themeGetList(force?: boolean) {
        if (this._state.loadingThemes === true || (this._state.themesListDef != null && force !== true)) {
            return this._state.themesListDef;
        }

        const def = $.Deferred<any>();

        this._state.loadingThemes = true;

        apiCall(this.actions, 'theme_GetList', {}, null, def);

        const result = def.then((resp: any) => {
            resp.result = Utilities.sortByName(resp.result);

            this._state.themesList = resp.result;

            const defs = _.map(resp.result, (obj) => {
                const id = obj.id;

                this._state.themesHash[id] = obj;
                return this.themeGet(id, true);
            });

            return $.when.apply($, defs);
        });

        result.done((resp) => {
            this.actions.themeGetListFinish(true);
        });

        result.fail((resp) => {
            this._state.themesList = [];
            this.actions.themeGetListFinish(false);
        });

        this._state.themesListDef = result;

        return result;
    }

    private themeGetListFinish(success) {
        this._state.loadingThemes = false;
    }

    private themeCreate(_def, name, data) {
        const def = $.Deferred<any>();

        if (this._state.creatingTheme === true) {
            _def.reject();
            return;
        }

        this._state.creatingTheme = true;

        apiCall(
            this.actions,
            'theme_Create',
            {
                name: name,
                data: JSON.stringify(data)
            },
            null,
            def
        );

        def.done((resp) => {
            this._state.themesData[resp.result] = data;
            this._state.themesType[resp.result] = appTheme.getThemeType(data);

            const obj = {
                id: resp.result,
                name: name,
                type: this._state.themesType[resp.result]
            };

            this._state.themesList.push(obj);
            this._state.themesHash[resp.result] = obj;
            this._state.themesList = Utilities.sortByName(this._state.themesList);
            this.actions.themeCreateFinish(true, name, data);
            _def.resolve();
        });

        def.fail((resp) => {
            this.actions.themeCreateFinish(false, name, data);
            _def.reject();
        });
    }

    private themeCreateFinish(success, name, data) {
        this._state.creatingTheme = false;
    }

    private themeUpdate(_def, id, name: string, data) {
        if (this._state.updatingTheme[id] === true) {
            _def.reject();
            return;
        }

        this._state.updatingTheme[id] = true;

        const defUpdate = apiCall(this.actions, 'theme_Update', {
            id: id,
            data: JSON.stringify(data)
        });

        const defRename = apiCall(this.actions, 'theme_Rename', {
            id: id,
            NewName: name
        });

        $.when(defUpdate, defRename)
            .done((resp) => {
                this._state.themesData[id] = data;
                this._state.themesType[id] = appTheme.getThemeType(data);

                const obj = this._state.themesHash[id];

                if (obj != null) {
                    obj.type = this._state.themesType[id];
                }

                const find = _.find(this._state.themesList, (theme) => {
                    return theme.id === id;
                });

                if (find != null) {
                    find.name = name;
                }

                this._state.themesList = Utilities.sortByName(this._state.themesList);
                this.actions.themeUpdateFinish(true, id, name, data);
                _def.resolve();
            })
            .fail((resp) => {
                this.actions.themeUpdateFinish(false, id, name, data);

                _def.reject();
            });
    }

    private themeUpdateFinish(success, id, name, data) {
        delete this._state.updatingTheme[id];
    }

    private themeRename(id, NewName) {
        const def = $.Deferred<any>();

        if (this._state.renamingTheme[id] === true) {
            return;
        }

        this._state.renamingTheme[id] = true;

        apiCall(
            this.actions,
            'theme_Rename',
            {
                id: id,
                NewName: NewName
            },
            null,
            def
        );

        def.done((resp) => {
            const find = _.find<any>(
                this._state.themesList,

                (theme) => {
                    return theme.id === id;
                }
            );

            if (find != null) {
                find.name = NewName;
            }

            const obj = this._state.themesHash[id];

            if (obj != null) {
                obj.name = NewName;
            }

            this._state.themesList = Utilities.sortByName(this._state.themesList);
            this.actions.themeRenameFinish(true, id, NewName);
        });

        def.fail((resp) => {
            this.actions.themeRenameFinish(false, id, NewName);
        });
    }

    private themeRenameFinish(success, id, NewName) {
        delete this._state.renamingTheme[id];
    }

    private themeDelete(id) {
        const def = $.Deferred<any>();

        if (this._state.deletingTheme[id] === true) {
            return;
        }

        this._state.deletingTheme[id] = true;

        apiCall(
            this.actions,
            'theme_Delete',
            {
                id: id
            },
            null,
            def
        );

        def.done((resp) => {
            delete this._state.themesData[id];
            delete this._state.themesType[id];
            delete this._state.themesHash[id];

            this._state.themesList = _.filter(
                this._state.themesList,

                (theme) => {
                    return theme.id !== id;
                }
            );

            this.actions.themeDeleteFinish(true, id);
        });

        def.fail((resp) => {
            this.actions.themeDeleteFinish(false, id);
        });
    }

    private themeDeleteFinish(success, id) {
        delete this._state.deletingTheme[id];
    }

    protected eventsHandler(payload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const a: Action = payload.action;
        const _case = checkAction;

        do {
            if (_case<I.THEME_GET>(a, Constants.THEME_GET)) {
                this.themeGet(a.id);
                this.emitChange();
                break;
            }

            if (_case<I.THEME_GET_FINISH>(a, Constants.THEME_GET_FINISH)) {
                this.themeGetFinish(a.success, a.id);
                this.emitChange();
                break;
            }

            if (_case<I.THEME_GET_LIST>(a, Constants.THEME_GET_LIST)) {
                this.themeGetList(a.force);
                this.emitChange();
                break;
            }

            if (_case<I.THEME_GET_LIST_FINISH>(a, Constants.THEME_GET_LIST_FINISH)) {
                this.themeGetListFinish(a.success);
                this.emitChange();
                break;
            }

            if (_case<I.THEME_CREATE>(a, Constants.THEME_CREATE)) {
                this.themeCreate(a.def, a.name, a.data);
                this.emitChange();
                break;
            }

            if (_case<I.THEME_CREATE_FINISH>(a, Constants.THEME_CREATE_FINISH)) {
                this.themeCreateFinish(a.success, a.name, a.data);
                this.emitChange();
                break;
            }

            if (_case<I.THEME_UPDATE>(a, Constants.THEME_UPDATE)) {
                this.themeUpdate(a.def, a.id, a.name, a.data);
                this.emitChange();
                break;
            }

            if (_case<I.THEME_UPDATE_FINISH>(a, Constants.THEME_UPDATE_FINISH)) {
                this.themeUpdateFinish(a.success, a.id, a.name, a.data);
                this.emitChange();
                break;
            }

            if (_case<I.THEME_RENAME>(a, Constants.THEME_RENAME)) {
                this.themeRename(a.id, a.NewName);
                this.emitChange();
                break;
            }

            if (_case<I.THEME_RENAME_FINISH>(a, Constants.THEME_RENAME_FINISH)) {
                this.themeRenameFinish(a.success, a.id, a.NewName);
                this.emitChange();
                break;
            }

            if (_case<I.THEME_DELETE>(a, Constants.THEME_DELETE)) {
                this.themeDelete(a.id);
                this.emitChange();
                break;
            }

            if (_case<I.THEME_DELETE_FINISH>(a, Constants.THEME_DELETE_FINISH)) {
                this.themeDeleteFinish(a.success, a.id);
                this.emitChange();
                break;
            }
        } while (false);

        return true;
    }

    constructor(glob, dispatcher: Dispatcher, actions: AppActions) {
        super(glob, dispatcher, actions, ThemeStore._name);

        this._state = _.extend(ThemeStore.getInitialState(), this._state);
    }

    static get(glob, dispatcher: Dispatcher, actions: AppActions) {
        return new ThemeStore(glob, dispatcher, actions);
    }
}
